import { Info } from "lucide-react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

export type LoadRatioChartPoint = {
  date: string;
  label: string;
  value: number;
};

type LoadRatioWeekChartProps = {
  data: LoadRatioChartPoint[];
  loading?: boolean;
  weekRangeLabel?: string | null;
  gradientId?: string;
};

export function LoadRatioChartTitle() {
  return (
    <div className="flex min-w-0 items-start gap-2">
      <div className="min-w-0">
        <p className="text-sm font-medium text-white leading-snug">
          Training load ratio
        </p>
        <p className="text-xs text-gray-light mt-0.5">
          7-day average ÷ 28-day average · this week
        </p>
      </div>
      <HoverCard openDelay={200} closeDelay={150}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            className="mt-0.5 shrink-0 rounded-full text-gray-light outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label="How is training load ratio calculated?"
          >
            <Info className="h-4 w-4" aria-hidden />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          align="start"
          className="w-80 border-primary/20 bg-dark-card text-white text-sm space-y-2"
        >
          <p className="leading-snug">
            Each point compares your average daily training load over the last{" "}
            <span className="text-primary">7 days</span> to your average over the
            last <span className="text-primary">28 days</span>.
          </p>
          <p className="text-xs text-gray-light leading-snug">
            Around <span className="text-white">1.0</span> means recent training
            matches your monthly baseline. Below 1.0 is lighter; above 1.0 is
            heavier. Days with no logged workouts count as zero load.
          </p>
        </HoverCardContent>
      </HoverCard>
    </div>
  );
}

export function LoadRatioWeekChart({
  data,
  loading = false,
  weekRangeLabel,
  gradientId = "loadRatioArea",
}: LoadRatioWeekChartProps) {
  if (loading) {
    return (
      <div className="text-sm text-gray-light py-6 text-center">Loading…</div>
    );
  }

  return (
    <div className="space-y-2">
      {weekRangeLabel ? (
        <p className="text-xs text-gray-light text-right">{weekRangeLabel}</p>
      ) : null}
      <ChartContainer
        config={{ loadRatio: { label: "Load ratio", color: "hsl(73 100% 60%)" } }}
        className="h-48 w-full"
      >
        <ComposedChart data={data} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-loadRatio)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--color-loadRatio)" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 4" stroke="#2E2E2E" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            stroke="#6F6F6F"
          />
          <YAxis
            domain={[0, "auto"]}
            allowDecimals
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            stroke="#6F6F6F"
            width={36}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelKey="loadRatio"
                formatter={(value) => [
                  typeof value === "number" ? value.toFixed(2) : value,
                  "Load ratio",
                ]}
              />
            }
          />
          <Area type="monotone" dataKey="value" stroke="none" fill={`url(#${gradientId})`} />
          <Line
            type="monotone"
            dataKey="value"
            stroke="var(--color-loadRatio)"
            strokeWidth={3}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
          <ReferenceLine
            y={1}
            stroke="#6F6F6F"
            strokeDasharray="3 3"
            strokeWidth={1}
            label={{
              value: "1.0 baseline",
              position: "right",
              fill: "#6F6F6F",
              fontSize: 11,
            }}
          />
        </ComposedChart>
      </ChartContainer>
    </div>
  );
}
